import { Text } from '@lmc/ui-kit/src/general'
import dayjs from 'dayjs'
import type { ReactNode } from 'react'
import styled from 'styled-components'
import Administered from '../iconsV2/Administered'
import Discontinued from '../iconsV2/Discontinued'
import Missed from '../iconsV2/Missed'
import NotScheduledOrNotNeeded from '../iconsV2/NotScheduledOrNotNeeded'
import Paused from '../iconsV2/Paused'
import {
  AdministrationStatus,
  type MarChartAdministration,
  type MarChartCell,
  type MarChartData,
  type MarChartDateStatus,
  type MarChartMedication,
  type MarChartOutcome,
  type MedicationTableData,
  type MedicationTableRow
} from '../types'

const StyledCode = styled(Text)`
  font-weight: 600;
  color: ${({ theme }) => theme.color.neutral.color3};
`

export type AdministrationStatusDisplay = {
  display: ReactNode
  translationKey: string
  /**
   * Short outcome code as printed on the MAR chart ('R', 'N/V', …). Only the
   * lettered outcomes have one — the rest are shown as an icon.
   */
  code?: string
}

/** An outcome shown as its short code, so the code has a single source. */
const codedStatus = (
  code: string,
  translationKey: string
): AdministrationStatusDisplay => ({
  display: <StyledCode level={2}>{code}</StyledCode>,
  translationKey,
  code
})

export const getAdministrationStatusDisplay = (
  status: AdministrationStatus
): AdministrationStatusDisplay => {
  switch (status) {
    case AdministrationStatus.GIVEN:
      return {
        display: <Administered />,
        translationKey: 'ADMINISTERED'
      }
    case AdministrationStatus.VERBAL_PROMPT:
      return codedStatus('VP', 'VERBAL_PROMPT')
    case AdministrationStatus.ABSENT:
      return codedStatus('A', 'ABSENT')
    case AdministrationStatus.OTHER:
      return codedStatus('O', 'OTHER')
    case AdministrationStatus.ALREADY_TAKEN:
      return codedStatus('AT', 'ALREADY_TAKEN')
    case AdministrationStatus.NOT_NEEDED:
      return {
        display: <NotScheduledOrNotNeeded />,
        translationKey: 'NOT_SCHEDULED_OR_NOT_NEEDED'
      }
    case AdministrationStatus.LEFT_OUT:
      return codedStatus('LO', 'LEFT_OUT')
    case AdministrationStatus.PAUSED:
      return {
        display: <Paused />,
        translationKey: 'PAUSED'
      }
    case AdministrationStatus.NAUSEA_VOMITING:
      return codedStatus('N/V', 'NAUSEA_OR_VOMITING')
    case AdministrationStatus.DISCONTINUED:
      return {
        display: <Discontinued />,
        translationKey: 'DISCONTINUED'
      }
    case AdministrationStatus.REFUSED:
      return codedStatus('R', 'REFUSED')
    case AdministrationStatus.MISSED:
      return {
        display: <Missed />,
        translationKey: 'MISSED'
      }
    case AdministrationStatus.REFUSED_DESTROYED:
      return codedStatus('RD', 'REFUSED_AND_DESTROYED')
    case AdministrationStatus.PRN_AVAILABLE:
      return {
        display: <Text>-</Text>,
        translationKey: 'PRN_AVAILABLE'
      }
    default:
      return {
        display: <Administered />,
        translationKey: 'ADMINISTERED'
      }
  }
}

export const getAdministrationSubtitle = ({
  status,
  doseQuantity,
  unit,
  quantityChange,
  stockChanged,
  t
}: {
  status: AdministrationStatus | undefined
  doseQuantity: number
  unit: string
  quantityChange?: number
  stockChanged?: boolean | string
  t: (key: string) => string
}): string | undefined => {
  if (!status) {
    return undefined
  }

  const stockDidNotChange = stockChanged === false || stockChanged === 'No'

  if (quantityChange == null && stockDidNotChange) {
    return t('NO_CHANGE_TO_STOCK')
  }

  const absQuantityChange =
    quantityChange != null ? Math.abs(quantityChange) : undefined
  const effectiveDose = absQuantityChange ?? doseQuantity

  if (effectiveDose === 0) {
    return t('NO_CHANGE_TO_STOCK')
  }

  const unitText = unit ? ` ${unit}` : ''

  return `${effectiveDose}${unitText}`
}

export const getAllAdministrationStatuses = (): AdministrationStatus[] => {
  const excludedStatuses = [
    AdministrationStatus.SCHEDULED,
    AdministrationStatus.OVERDUE,
    AdministrationStatus.PRN_AVAILABLE,
    AdministrationStatus.UNKNOWN,
    AdministrationStatus.NOT_NEEDED,
    AdministrationStatus.MISSED,
    AdministrationStatus.PAUSED,
    AdministrationStatus.DISCONTINUED
  ]

  return Object.values(AdministrationStatus).filter(
    (status) => !excludedStatuses.includes(status)
  ) as AdministrationStatus[]
}

export const getAdministrationStatusOptions = (
  t: (key: string) => string
): Array<{ value: AdministrationStatus; label: string }> => {
  return getAllAdministrationStatuses().map((status) => {
    const display = getAdministrationStatusDisplay(status)
    return {
      value: status,
      label: t(display.translationKey)
    }
  })
}

/**
 * The dose fields `getStockChangedText` reads. Kept structural so both
 * `MarChartAdministration` and `StockRecord['administration']` satisfy it.
 */
type StockChangedDoseSource = {
  doseQuantity?: number
  dose?: string | number
  medication?: MarChartAdministration['medication']
  administration?: Pick<
    NonNullable<MarChartAdministration['administration']>,
    'doseGiven'
  >
}

const DOSE_WITH_UNIT = /^[\d.]+\s+(.+)$/

/**
 * The unit a dose carries in its own text — "8 Puffs" → "Puffs". A medication
 * with no dose unit configured records it nowhere else, so this is the only
 * dose unit those records have. Undefined when there is none to pull out,
 * which also marks a dose that still needs a unit appended to it.
 */
export const getUnitFromDoseText = (
  dose?: string | number | null
): string | undefined =>
  typeof dose === 'string' ? dose.trim().match(DOSE_WITH_UNIT)?.[1] : undefined

export const getStockChangedText = ({
  administration,
  dateStatus,
  unit = '',
  doseUnit = unit,
  stockChanged,
  quantityChange,
  stockAffected,
  t
}: {
  /**
   * Either a MAR chart administration or the flattened `StockRecord`
   * administration — only the dose fields below are read, so both fit.
   */
  administration?: StockChangedDoseSource
  dateStatus?: Pick<MarChartDateStatus, 'doseQuantity'>
  /** The unit stock moves in — what "removed N from stock" counts. */
  unit?: string
  /**
   * The unit the dose is measured in, for the stock-tracking-off wording. Only
   * the same as `unit` when the medication is dosed in whole stock units: a
   * medication stocked by the box can be dosed in puffs. Defaults to `unit`.
   */
  doseUnit?: string
  stockChanged?: boolean | string
  quantityChange?: number
  stockAffected?: boolean
  t: (key: string) => string
}): string => {
  if (stockAffected === false) {
    const doseGiven = administration?.administration?.doseGiven
    if (doseGiven) {
      // Nothing left stock on this branch, so the number is a dose and takes
      // the dose unit — "8 Puffs was used", not "8 box was used" (ROST-3829) —
      // and nothing at all when the dose text already carries its own.
      const carriesOwnUnit = getUnitFromDoseText(doseGiven) !== undefined
      const unitText = carriesOwnUnit || !doseUnit ? '' : ` ${doseUnit}`
      return `${doseGiven}${unitText} ${t('WAS_USED')}`
    }
    return t('NO')
  }

  if (stockChanged === false || stockChanged === 'No') {
    return t('NO')
  }

  const absQuantityChange =
    quantityChange != null ? Math.abs(quantityChange) : undefined

  const doseQuantity = Number(
    absQuantityChange ||
      administration?.doseQuantity ||
      administration?.medication?.doseQuantity ||
      dateStatus?.doseQuantity ||
      administration?.dose ||
      0
  )

  if (doseQuantity > 0) {
    const unitText = unit ? ` ${unit}` : ''
    return `${t('YES')}, removed ${doseQuantity}${unitText} from stock`
  }

  return t('NO')
}

const formatDose = (dose: string | number | object): string => {
  if (typeof dose === 'string') {
    return dose
  }
  if (typeof dose === 'number') {
    return String(dose)
  }
  if (typeof dose === 'object' && dose !== null) {
    return JSON.stringify(dose)
  }
  return ''
}

// Treat null, undefined, and empty string all as "no value" — the API can
// return any of the three when an alt dose is not configured.
const isMissingAltDose = (value: string | number | undefined | null): boolean =>
  value == null || value === ''

const resolveAltDose = (medication: MarChartMedication): string | undefined => {
  if (isMissingAltDose(medication.altDose)) {
    return undefined
  }
  const unit = medication.quantityType ? ` ${medication.quantityType}` : ''
  return `${formatDose(medication.altDose as string | number)}${unit}`.trim()
}

export const transformMarChartData = (
  data: MarChartData | null | undefined,
  type: 'scheduled' | 'prn',
  startDate: dayjs.Dayjs,
  endDate: dayjs.Dayjs
): MedicationTableRow[] => {
  if (!data || !data.medications) {
    return []
  }

  const rows: MedicationTableRow[] = []
  let rowIndex = 0

  const filteredMedications = data.medications.filter(
    (med) => med.prn === (type === 'prn')
  )

  filteredMedications.forEach((medication) => {
    const sortedTimings = [...medication.roundTimings].sort((a, b) =>
      (a.timingTime || '').localeCompare(b.timingTime || '')
    )
    sortedTimings.forEach((timing) => {
      const currentStock = medication.currentStock || 0
      const stockAlertLevel = medication.stockAlertLevel

      const medicationData: MedicationTableData = {
        name: medication.medicationName,
        currentStock,
        startDate: dayjs(medication.startDate).format('DD-MM-YYYY'),
        endDate: medication.endDate
          ? dayjs(medication.endDate).format('DD-MM-YYYY')
          : '',
        isControlledDrug: medication.controlledDrug,
        lowStock:
          stockAlertLevel !== undefined &&
          currentStock > 0 &&
          currentStock <= stockAlertLevel,
        outOfStock: currentStock === 0,
        stockUnit: medication.stockUnit || medication.quantityType || '',
        quantityType: medication.quantityType || '',
        stockAlertLevel,
        isDiscontinued:
          medication.discontinued === true ||
          (medication.endDate
            ? dayjs(medication.endDate).isBefore(dayjs(), 'day')
            : false),
        secondSignatureRequired: medication.secondSignatureRequired
      }

      const row: MedicationTableRow = {
        key: `med-${medication.residentMedicationId}-${timing.roundTimingId}-${rowIndex}`,
        medication: medicationData,
        dose: timing.customDose
          ? String(timing.customDose)
          : `${formatDose(timing.dose)}${medication.quantityType ? ` ${medication.quantityType}` : ''}`.trim(),
        altDose: resolveAltDose(medication),
        residentMedicationId: medication.residentMedicationId
      }

      if (type === 'scheduled') {
        row.hour =
          timing.timingTime ||
          timing.timingName ||
          timing.dateStatuses?.find((ds) => ds.timingTime || ds.timingName)
            ?.timingTime ||
          ''
      }

      const dateMap: Record<string, MarChartCell> = {}

      timing.dateStatuses.forEach((dateStatus) => {
        const dateKey = dayjs(dateStatus.date).format('DD-MM-YYYY')
        const administrationStatus = dateStatus.status as AdministrationStatus

        if (!administrationStatus) {
          return
        }

        if (administrationStatus === AdministrationStatus.SCHEDULED) {
          if (!dateMap[dateKey]) {
            dateMap[dateKey] = {
              outcomes: []
            }
          }
          dateMap[dateKey].outcomes.push({
            action: 'SCHEDULED',
            date: dateKey
          })
          return
        }

        const statusDisplay =
          getAdministrationStatusDisplay(administrationStatus)

        if (!dateMap[dateKey]) {
          dateMap[dateKey] = {
            outcomes: []
          }
        }

        const outcome: MarChartOutcome = {
          action: statusDisplay.translationKey,
          date: dateKey
        }

        if (dateStatus.recordedBy) {
          outcome.recordDetails = {
            quantity:
              dateStatus.doseQuantity?.toString() || formatDose(timing.dose),
            timeRecorded:
              dateStatus.administeredTime ||
              dayjs(dateStatus.date).toISOString(),
            recordedById: dateStatus.recordedBy.id,
            recordedBy: `${dateStatus.recordedBy.name.first} ${dateStatus.recordedBy.name.last}`,
            recordedByPicture: dateStatus.recordedBy.picture,
            comment: dateStatus.details,
            comments: dateStatus.details ? [dateStatus.details] : [],
            witnesses: dateStatus.witnesses || []
          }
        }

        outcome.dateStatus = dateStatus

        if (dateStatus.administration) {
          outcome.administration = dateStatus.administration
        }

        dateMap[dateKey].outcomes.push(outcome)
      })

      Object.keys(dateMap).forEach((dateKey) => {
        row[dateKey] = dateMap[dateKey]
      })

      rows.push(row)
      rowIndex++
    })
  })

  return rows
}
